import { ReactElement, useRef, useState, FormEvent, ChangeEvent } from "react"
import { Input } from "@mui/material"
import ReCAPTCHA from "react-google-recaptcha"
import emailjs from "emailjs-com"
import styled from "@emotion/styled"
import { BsFillEnvelopeFill } from "react-icons/bs"
import { useTranslation } from "../TranslationProvider"
import { ContactFormData } from "../types"
import Button from "./Button"

interface RequiredFields {
  name: boolean
  phone: boolean
  email: boolean
  message: boolean
  captcha: boolean
}

export default function ContactForm(): ReactElement {
  const { t } = useTranslation()

  const formDefault: ContactFormData = {
    name: "",
    phone: "",
    email: "",
    message: "",
  }
  const requiredDefault: RequiredFields = {
    name: true,
    phone: true,
    email: true,
    message: true,
    captcha: true,
  }

  const [form, setForm] = useState<ContactFormData>(formDefault)
  const [required, setRequired] = useState<RequiredFields>(requiredDefault)
  const [isSuccess, setSuccess] = useState<boolean>(false)
  const [formatError, setFormatError] = useState<{
    phone: boolean
    email: boolean
  }>({ phone: false, email: false })

  const captchaRef = useRef<ReCAPTCHA>(null)

  const phoneRegex = /^\D*(\d\D*){10,}$/ // at least 10 digits, allows spaces, dashes, etc.
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  function formChange(
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  function showSuccess(): void {
    setSuccess(true)

    setTimeout(() => {
      setSuccess(false)
    }, 3000)
  }

  const formSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()

    const { name, phone, email, message } = form
    const token = captchaRef.current?.getValue()

    // Check required
    const requiredState = {
      name: !!name,
      phone: !!phone,
      email: !!email,
      message: !!message,
      captcha: !!token,
    }
    setRequired(requiredState)

    // Check format errors (only if filled)
    const phoneFormat = !!phone && !phoneRegex.test(phone)
    const emailFormat = !!email && !emailRegex.test(email)
    setFormatError({ phone: phoneFormat, email: emailFormat })

    // If any required or format error, do not submit
    if (
      !name ||
      !phone ||
      !email ||
      !message ||
      !token ||
      phoneFormat ||
      emailFormat
    ) {
      return
    }

    try {
      await emailjs.sendForm(
        process.env.REACT_APP_EMAIL_SERVICE_ID!,
        process.env.REACT_APP_EMAIL_TEMPLATE_ID!,
        e.currentTarget,
        process.env.REACT_APP_EMAIL_USER_ID!
      )
    } catch (error) {
      console.error(error)
    } finally {
      showSuccess()
      setForm(formDefault)
      setRequired(requiredDefault)
      setFormatError({ phone: false, email: false })
      captchaRef.current?.reset()
    }
  }

  return (
    <FormWrapper onSubmit={formSubmit}>
      <InputWrapper>
        <StyledInput
          type="text"
          name="name"
          value={form.name}
          onChange={formChange}
          placeholder={t("contactForm.name")}
        />

        {!required.name && (
          <RequiredWarning>{t("contactForm.required")}</RequiredWarning>
        )}
      </InputWrapper>

      <InputWrapper>
        <StyledInput
          type="text"
          name="phone"
          value={form.phone}
          onChange={formChange}
          placeholder={t("contactForm.phone")}
        />

        {!required.phone && (
          <RequiredWarning>{t("contactForm.required")}</RequiredWarning>
        )}
        {required.phone && formatError.phone && (
          <RequiredWarning>{t("contactForm.phoneFormat")}</RequiredWarning>
        )}
      </InputWrapper>

      <InputWrapper>
        <StyledInput
          type="text"
          name="email"
          value={form.email}
          onChange={formChange}
          placeholder={t("contactForm.email")}
        />

        {!required.email && (
          <RequiredWarning>{t("contactForm.required")}</RequiredWarning>
        )}
        {required.email && formatError.email && (
          <RequiredWarning>{t("contactForm.emailFormat")}</RequiredWarning>
        )}
      </InputWrapper>

      <InputWrapper>
        <StyledInput
          multiline={true}
          minRows={6}
          type="text"
          name="message"
          value={form.message}
          onChange={formChange}
          placeholder={t("contactForm.message")}
        />

        {!required.message && (
          <RequiredWarning>{t("contactForm.required")}</RequiredWarning>
        )}
      </InputWrapper>

      {!isSuccess && (
        <InputWrapper style={{ width: "fit-content" }}>
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY!}
            ref={captchaRef}
          />

          {!required.captcha && (
            <RequiredWarning style={{ width: "100%" }}>
              {t("contactForm.required")}
            </RequiredWarning>
          )}
        </InputWrapper>
      )}

      {isSuccess && (
        <SuccessWarning>{t("contactForm.confirmation")}</SuccessWarning>
      )}

      <Button
        type="submit"
        text={t("contactForm.submit")}
        icon={<BsFillEnvelopeFill size={20} />}
        variant="rounded"
      />
    </FormWrapper>
  )
}

const FormWrapper = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`

const InputWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  user-select: none;
`

const StyledInput = styled(Input)`
  background-color: var(--white);
  padding: 0.2rem 1rem;
  width: 90%;
  border-radius: 0.3rem;
  color: var(--tertiary);
  font-family: "Filson Soft", Arial, sans-serif;
`

const RequiredWarning = styled.h5`
  width: 90%;
  text-align: left;
  padding: 0.2em 0 0 1em;
  color: var(--warning);
  margin: 0;
`

const SuccessWarning = styled.h3`
  width: 90%;
  color: var(--success);
  padding: 0;
  margin: 0;
`
